import { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { OrderItemRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { IconArrowLeft, IconInbox } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { Skeleton } from '@/components/States';
import { usePortal } from '@/features/portal/PortalContext';
import { OrderStatusBadge, PaymentStatusBadge } from '@/features/portal/badges';
import { fetchOrder, portalKeys } from '@/features/portal/api';
import { amountSign, formatCount, formatMoney, formatPosDate, formatPosTime } from '@/lib/format';

function SummaryRow({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className={`text-sm ${strong ? 'font-semibold text-slate-900' : 'text-slate-500'}`}>{label}</dt>
      <dd className={`text-sm tabular-nums ${strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
        {value}
      </dd>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-5 py-2.5">
      <dt className="text-[11px] font-semibold uppercase leading-none tracking-[0.08em] text-slate-400">{label}</dt>
      <dd className="mt-1 text-[15px] font-semibold leading-snug text-slate-900">{value}</dd>
    </div>
  );
}

export function OrderDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const location = useLocation();
  const { currency, multiBranch } = usePortal();

  const orderQuery = useQuery({
    queryKey: portalKeys.order(id),
    queryFn: ({ signal }) => fetchOrder(id, signal),
    enabled: id !== '',
  });

  const columns = useMemo<Array<Column<OrderItemRow>>>(
    () => [
      {
        key: 'productName',
        header: 'Item',
        cell: (row) => (
          <div className="min-w-0">
            <p className="font-medium text-slate-900">{row.productName}</p>
            {row.notes ? <p className="mt-0.5 text-xs text-slate-400">{row.notes}</p> : null}
          </div>
        ),
      },
      {
        key: 'quantity',
        header: 'Qty',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatCount(row.quantity),
      },
      {
        key: 'unitPrice',
        header: 'Unit price',
        align: 'right',
        cellClassName: 'tabular-nums',
        cell: (row) => formatMoney(row.unitPrice, currency),
      },
      {
        key: 'total',
        header: 'Total',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => formatMoney(row.total, currency),
      },
    ],
    [currency],
  );

  const backLink = (
    <Link
      to={{ pathname: '/orders', search: location.search }}
      className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
    >
      <IconArrowLeft className="h-4 w-4" />
      Back to orders
    </Link>
  );

  return (
    <AsyncSection
      state={orderQuery}
      skeleton={
        <>
          <div className="mb-6 space-y-3">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-7 w-48" />
          </div>
          <div className="card overflow-hidden">
            <TableSkeleton rows={4} columns={4} />
          </div>
        </>
      }
      errorTitle="Could not load this order"
    >
      {(order) => (
        <>
          <PageHeader
            eyebrow={backLink}
            title={`Order ${order.orderNumber}`}
            description={`${formatPosDate(order.date)} · ${formatPosTime(order.time)}${
              multiBranch && order.branchName ? ` · ${order.branchName}` : ''
            }`}
            meta={
              <span className="flex flex-wrap items-center gap-2">
                <OrderStatusBadge status={order.status} />
                <PaymentStatusBadge status={order.paymentStatus} />
              </span>
            }
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <SectionCard headingId="order-items" title="Items" count={order.items.length}>
                {order.items.length === 0 ? (
                  <div className="flex items-center gap-3 px-5 py-8 text-sm text-slate-500">
                    <IconInbox className="h-5 w-5 text-slate-300" />
                    This order has no item lines.
                  </div>
                ) : (
                  <DataTable
                    caption={`Items on order ${order.orderNumber}`}
                    columns={columns}
                    rows={order.items}
                    rowKey={(row, index) => `${row.productName}-${index}`}
                  />
                )}
              </SectionCard>
            </div>

            <div className="space-y-6">
              <section className="card overflow-hidden" aria-labelledby="order-totals">
                <div className="card-header">
                  <h2 id="order-totals" className="text-sm font-semibold text-slate-900">
                    Totals
                  </h2>
                </div>
                <dl className="divide-y divide-slate-100 py-1">
                  <SummaryRow label="Subtotal" value={formatMoney(order.subtotal, currency)} />
                  {amountSign(order.discountAmount) !== 0 ? (
                    <SummaryRow label="Discount" value={formatMoney(order.discountAmount, currency)} />
                  ) : null}
                  <SummaryRow label="Tax" value={formatMoney(order.taxAmount, currency)} />
                  {amountSign(order.deliveryCharge) !== 0 ? (
                    <SummaryRow label="Delivery" value={formatMoney(order.deliveryCharge, currency)} />
                  ) : null}
                  <SummaryRow label="Total" value={formatMoney(order.total, currency)} strong />
                </dl>
              </section>

              <section className="card overflow-hidden" aria-labelledby="order-details">
                <div className="card-header">
                  <h2 id="order-details" className="text-sm font-semibold text-slate-900">
                    Details
                  </h2>
                </div>
                <dl className="divide-y divide-slate-100 py-1">
                  <DetailRow label="Customer" value={order.customerName || 'Walk-in'} />
                  <DetailRow label="Order type" value={order.orderType || '—'} />
                  <DetailRow label="Payment method" value={order.paymentMethod || '—'} />
                  <DetailRow label="Cashier" value={order.cashier || '—'} />
                  {order.waiter ? <DetailRow label="Waiter" value={order.waiter} /> : null}
                  {order.tableLabel ? <DetailRow label="Table" value={order.tableLabel} /> : null}
                  {multiBranch ? <DetailRow label="Branch" value={order.branchName || '—'} /> : null}
                </dl>
              </section>
            </div>
          </div>
        </>
      )}
    </AsyncSection>
  );
}
